import { useEffect, useRef, useState } from "react";
import { vpAxios, money } from "../api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Tag, Calendar } from "lucide-react";

const DIAS = [
  { v: 0, l: "Seg" }, { v: 1, l: "Ter" }, { v: 2, l: "Qua" },
  { v: 3, l: "Qui" }, { v: 4, l: "Sex" }, { v: 5, l: "Sáb" }, { v: 6, l: "Dom" },
];

export default function CuponsPage() {
  const [rows, setRows] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = () => vpAxios.get("/vaipede/cupons").then(r => setRows(r.data));
  useEffect(() => {
    load();
    vpAxios.get("/vaipede/produtos").then(r => setProdutos(r.data)).catch(() => {});
  }, []);

  const salvar = async (data) => {
    try {
      if (edit) await vpAxios.put(`/vaipede/cupons/${edit.id}`, data);
      else await vpAxios.post("/vaipede/cupons", data);
      toast.success("Salvo"); setOpen(false); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir cupom?")) return;
    await vpAxios.delete(`/vaipede/cupons/${id}`); load();
  };

  return (
    <div data-testid="vaipede-cupons">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black">Cupons e Promoções</h1>
          <p className="text-sm text-neutral-500">Dias promocionais que aparecem no cardápio digital</p>
        </div>
        <button onClick={() => { setEdit(null); setOpen(true); }} className="px-4 py-2.5 rounded-xl bg-[#EA1D2C] text-white font-bold text-sm flex items-center gap-2" data-testid="vaipede-cup-add">
          <Plus className="w-4 h-4"/> Novo cupom
        </button>
      </div>

      {rows.length === 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-10 text-center">
          <Tag className="w-8 h-8 text-neutral-300 mx-auto mb-2"/>
          <p className="text-sm text-neutral-500">Crie promoções como "Quinta da Pizza" com quantidade limitada e banner no cardápio.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map(c => {
          const restantes = c.limite_uso > 0 ? (c.limite_uso - c.usados) : null;
          return (
            <div key={c.id} className="bg-white rounded-2xl border-l-4 p-4" style={{borderLeftColor: c.banner_cor || "#EA1D2C"}}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl">{c.banner_emoji || "🎁"}</span>
                    <b className="font-black truncate">{c.nome}</b>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.ativo ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-500"}`}>
                      {c.ativo ? "Ativo" : "Off"}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mb-1">Código: <span className="font-mono font-bold">{c.codigo}</span></div>
                  <div className="text-xs">
                    Desconto: <b>{c.tipo_desconto === "percentual" ? `${c.valor}%` : money(c.valor)}</b>
                    {c.valor_minimo_pedido > 0 && <span className="text-neutral-500"> · mín {money(c.valor_minimo_pedido)}</span>}
                  </div>
                  {(c.dias_semana && c.dias_semana.length > 0) && (
                    <div className="text-xs text-blue-700 mt-1 flex items-center gap-1">
                      <Calendar className="w-3 h-3"/> {c.dias_semana.map(d => DIAS.find(x => x.v === d)?.l).join(", ")}
                    </div>
                  )}
                  <div className="text-xs mt-1">
                    Usos: <b>{c.usados}</b>{c.limite_uso > 0 && <> / {c.limite_uso}</>}
                    {restantes !== null && restantes > 0 && <span className="text-emerald-700"> · {restantes} vagas</span>}
                    {restantes === 0 && <span className="text-red-600"> · esgotado</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => { setEdit(c); setOpen(true); }} className="p-1.5 text-neutral-500"><Pencil className="w-4 h-4"/></button>
                  <button onClick={() => excluir(c.id)} className="p-1.5 text-red-500"><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {open && <CupomModal initial={edit} produtos={produtos} onClose={() => { setOpen(false); setEdit(null); }} onSave={salvar}/>}
    </div>
  );
}

function CupomModal({ initial, produtos, onClose, onSave }) {
  const bannerRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    codigo: initial?.codigo || "",
    nome: initial?.nome || "",
    descricao: initial?.descricao || "",
    tipo_desconto: initial?.tipo_desconto || "percentual",
    valor: String(initial?.valor ?? ""),
    limite_uso: String(initial?.limite_uso ?? "0"),
    dias_semana: initial?.dias_semana || [],
    produto_ids: initial?.produto_ids || [],
    ativo: initial?.ativo !== false,
    banner_cor: initial?.banner_cor || "#EA1D2C",
    banner_emoji: initial?.banner_emoji || "🍕",
    banner_url: initial?.banner_url || "",
    valor_minimo_pedido: String(initial?.valor_minimo_pedido ?? "0"),
  });
  const toggleDia = (v) => setF(x => ({...x, dias_semana: x.dias_semana.includes(v) ? x.dias_semana.filter(d => d !== v) : [...x.dias_semana, v]}));
  const toggleProduto = (id) => setF(x => ({
    ...x, produto_ids: x.produto_ids.includes(id) ? x.produto_ids.filter(p => p !== id) : [...x.produto_ids, id]
  }));
  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("Imagem muito grande (máx. 2.5MB)"); return; }
    setUploading(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await vpAxios.post("/vaipede/uploads/imagem", { data_url: dataUrl });
      setF(prev => ({ ...prev, banner_url: data.url }));
      toast.success("Banner enviado");
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const salvar = () => onSave({
    ...f,
    valor: parseFloat((f.valor || "0").replace(",", ".")) || 0,
    limite_uso: parseInt(f.limite_uso || "0") || 0,
    valor_minimo_pedido: parseFloat((f.valor_minimo_pedido || "0").replace(",", ".")) || 0,
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-black text-lg">{initial ? "Editar" : "Novo"} cupom</h2>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="space-y-2">
          <input value={f.nome} onChange={e => setF({...f, nome: e.target.value})} placeholder="Nome (ex: Quinta da Pizza)" className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-cup-nome"/>
          <input value={f.codigo} onChange={e => setF({...f, codigo: e.target.value.toUpperCase()})} placeholder="Código (ex: PIZZA30)" className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm font-mono uppercase" data-testid="vaipede-cup-codigo" disabled={!!initial}/>
          <textarea value={f.descricao} onChange={e => setF({...f, descricao: e.target.value})} placeholder="Descrição breve" rows={2} className="w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm resize-none"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={f.tipo_desconto} onChange={e => setF({...f, tipo_desconto: e.target.value})} className="px-3 py-2.5 rounded-xl border border-neutral-200 text-sm">
              <option value="percentual">% Percentual</option>
              <option value="fixo">R$ Fixo</option>
            </select>
            <input type="text" inputMode="decimal" value={f.valor} onChange={e => setF({...f, valor: e.target.value.replace(",", ".")})} placeholder="Valor" className="px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-cup-valor"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs">
              <span className="text-neutral-500">Limite de uso (0 = ilimitado)</span>
              <input type="text" inputMode="numeric" value={f.limite_uso} onChange={e => setF({...f, limite_uso: e.target.value.replace(/[^0-9]/g, "")})} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm" data-testid="vaipede-cup-limite"/>
            </label>
            <label className="text-xs">
              <span className="text-neutral-500">Pedido mínimo (R$)</span>
              <input type="text" inputMode="decimal" value={f.valor_minimo_pedido} onChange={e => setF({...f, valor_minimo_pedido: e.target.value.replace(",", ".")})} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-sm"/>
            </label>
          </div>
          <div>
            <span className="text-xs text-neutral-500">Dias que valem (vazio = todo dia)</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {DIAS.map(d => (
                <button key={d.v} onClick={() => toggleDia(d.v)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${f.dias_semana.includes(d.v) ? "bg-[#EA1D2C] text-white" : "bg-neutral-100"}`} data-testid={`vaipede-cup-dia-${d.v}`}>
                  {d.l}
                </button>
              ))}
            </div>
          </div>

          {/* Banner upload */}
          <div className="border border-neutral-200 rounded-xl p-3">
            <span className="text-xs text-neutral-500">Banner (aparece no cardápio digital nos dias configurados)</span>
            {f.banner_url && <img src={f.banner_url} alt="" className="w-full h-24 object-cover rounded-lg mt-2"/>}
            <input type="file" accept="image/*" ref={bannerRef} onChange={uploadBanner} className="hidden" data-testid="vaipede-cup-banner-input"/>
            <button onClick={() => bannerRef.current?.click()} disabled={uploading} className="mt-2 w-full py-2 rounded-xl bg-neutral-100 text-sm font-bold" data-testid="vaipede-cup-banner-btn">
              {uploading ? "Enviando..." : (f.banner_url ? "Trocar banner" : "Enviar imagem do banner")}
            </button>
            {f.banner_url && <button onClick={() => setF({...f, banner_url: ""})} className="text-xs text-red-500 mt-1">Remover banner</button>}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs">
              <span className="text-neutral-500">Emoji do banner</span>
              <input value={f.banner_emoji} onChange={e => setF({...f, banner_emoji: e.target.value})} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-neutral-200 text-lg text-center"/>
            </label>
            <label className="text-xs">
              <span className="text-neutral-500">Cor</span>
              <input type="color" value={f.banner_cor} onChange={e => setF({...f, banner_cor: e.target.value})} className="mt-1 w-full h-11 rounded-xl border border-neutral-200"/>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.ativo} onChange={e => setF({...f, ativo: e.target.checked})}/>
            Cupom ativo
          </label>

          {/* Produtos aplicáveis */}
          <div className="border border-neutral-200 rounded-xl p-3" data-testid="vaipede-cup-produtos">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-neutral-500">
                Produtos aplicáveis {f.produto_ids.length > 0 ? `(${f.produto_ids.length})` : "(todos)"}
              </span>
              {f.produto_ids.length > 0 && (
                <button onClick={() => setF({...f, produto_ids: []})} className="text-xs text-red-500" data-testid="vaipede-cup-limpar-prods">
                  Limpar (aplicar em todos)
                </button>
              )}
            </div>
            <p className="text-xs text-neutral-400 mb-2">
              Selecione produtos específicos para restringir o desconto. Deixe vazio para aplicar no cardápio inteiro.
            </p>
            <div className="max-h-40 overflow-auto space-y-1">
              {(produtos || []).length === 0 && <p className="text-xs text-neutral-400">Nenhum produto cadastrado.</p>}
              {(produtos || []).map(p => (
                <label key={p.id} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-lg cursor-pointer ${f.produto_ids.includes(p.id) ? "bg-red-50" : "hover:bg-neutral-50"}`}>
                  <input
                    type="checkbox"
                    checked={f.produto_ids.includes(p.id)}
                    onChange={() => toggleProduto(p.id)}
                    className="accent-[#EA1D2C]"
                    data-testid={`vaipede-cup-prod-${p.id}`}
                  />
                  <span className="flex-1 truncate">{p.nome}</span>
                  <span className="text-xs text-neutral-500">{money(p.preco)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <button onClick={salvar} className="w-full mt-4 py-3 rounded-xl bg-[#EA1D2C] text-white font-bold" data-testid="vaipede-cup-salvar">Salvar</button>
      </div>
    </div>
  );
}
